/* Base layout for status pages */
.root {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: var(--nexus-space-3xl);
  position: relative;
  overflow: hidden;
}

/* Subtle scanline overlay */
.root::before {
  content: '';
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    0deg,
    transparent,
    transparent 2px,
    rgba(124, 58, 237, 0.015) 2px,
    rgba(124, 58, 237, 0.015) 4px
  );
  pointer-events: none;
  animation: scanlines 8s linear infinite;
}

@keyframes scanlines {
  0% { transform: translateY(0); }
  100% { transform: translateY(4px); }
}

.content {
  text-align: center;
  max-width: 480px;
  position: relative;
  z-index: 1;
}

/* Glitchy large text effect */
.glitch {
  font-size: 8rem;
  font-weight: 900;
  line-height: 1;
  letter-spacing: -4px;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  margin-bottom: var(--nexus-space-md);
  animation: flicker 4s infinite;
}

.glitch::before,
.glitch::after {
  content: attr(data-text);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 0.6;
}

.glitch::before {
  animation: glitchShift 3s infinite;
  clip-path: inset(20% 0 40% 0);
}

.glitch::after {
  animation: glitchShift2 2.5s infinite;
  clip-path: inset(60% 0 10% 0);
}

/* Smaller glitch text for ComingSoon feature names */
.glitchSm {
  composes: glitch;
  font-size: 4rem;
  letter-spacing: -2px;
}

@keyframes flicker {
  0%, 95%, 100% { opacity: 1; }
  96% { opacity: 0.8; }
  97% { opacity: 1; }
  98% { opacity: 0.6; }
}

@keyframes glitchShift {
  0%, 90%, 100% { transform: translateX(0); }
  92% { transform: translateX(-3px); }
  94% { transform: translateX(3px); }
  96% { transform: translateX(-1px); }
}

@keyframes glitchShift2 {
  0%, 88%, 100% { transform: translateX(0); }
  90% { transform: translateX(2px); }
  93% { transform: translateX(-2px); }
}

/* Status label — small badge above the glitch text */
.statusLabel {
  display: inline-block;
  padding: var(--nexus-space-2xs) 0;
  font-size: var(--nexus-text-xs);
  font-weight: var(--nexus-weight-medium);
  color: #a78bfa;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: var(--nexus-space-md);
}

.title {
  margin-bottom: var(--nexus-space-xs);
}

.subtitle {
  margin-bottom: var(--nexus-space-xl);
  line-height: 1.6;
  min-height: 1.6em;
}

/* Fade transition for rotating messages */
.subtitleFade {
  animation: fadeInUp 0.4s ease-out;
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(6px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.actions {
  display: flex;
  gap: var(--nexus-space-sm);
  justify-content: center;
  margin-bottom: var(--nexus-space-lg);
}

.hint {
  opacity: 0.4;
}

/* Floating particles */
.particles {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.particle {
  position: absolute;
  width: 3px;
  height: 3px;
  border-radius: var(--nexus-radius-full);
  background: #7c3aed;
  opacity: 0;
  animation: float 6s ease-in-out infinite;
}

.particle:nth-child(1) {
  left: 10%;
  top: 20%;
  animation-delay: 0s;
  animation-duration: 7s;
}

.particle:nth-child(2) {
  left: 80%;
  top: 60%;
  animation-delay: 1s;
  animation-duration: 5s;
  background: #ec4899;
}

.particle:nth-child(3) {
  left: 30%;
  top: 80%;
  animation-delay: 2s;
  animation-duration: 8s;
}

.particle:nth-child(4) {
  left: 70%;
  top: 15%;
  animation-delay: 3s;
  animation-duration: 6s;
  background: #ec4899;
}

.particle:nth-child(5) {
  left: 50%;
  top: 50%;
  animation-delay: 4s;
  animation-duration: 7s;
}

.particle:nth-child(6) {
  left: 90%;
  top: 40%;
  animation-delay: 1.5s;
  animation-duration: 9s;
  background: #ec4899;
}

@keyframes float {
  0%, 100% {
    opacity: 0;
    transform: translateY(0) scale(1);
  }
  20% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.2;
    transform: translateY(-40px) scale(1.5);
  }
  80% {
    opacity: 0.3;
  }
}

/* Mobile */
@media (max-width: 768px) {
  .root {
    padding: var(--nexus-space-xl);
    min-height: 50vh;
  }

  .glitch {
    font-size: 5rem;
    letter-spacing: -2px;
  }

  .glitchSm {
    font-size: 2.5rem;
    letter-spacing: -1px;
  }
}
